import React from 'react';
import { Trans } from 'react-i18next';
import { renderFormElement } from '../UserRegistrationForm.utils';
import FormsFieldset from '../../../molecules/Form/components/FormsFieldset/FormsFieldset';
import FormsRow from '../../../molecules/Form/components/FormsRow/FormsRow';
import { ContentLink } from '../../../../_lib/components/ContentLink';
import { Namespace } from '../../../../translation/namespace';
import { ContentParagraph } from '../../../../_lib/components/ContentParagraph';
import { FormUtils, useFormContext } from '../../../molecules/Form';
import { REGISTRATION_TYPE } from '../UserRegistrationForm.enums';
import { Hyperlink, HyperlinkTarget } from '../../../atoms/Hyperlink';
import LiveRegion from '../../../atoms/LiveRegion/LiveRegion';
import { InteractionStatus } from '../../../../types/InteractionStatus';
import { Strong } from '../../../atoms/Strong/Strong';
import { UserRegistrationFormProps } from '../UserRegistrationForm';
import { FieldsetContactData } from './FieldsetContactData';
import { FormFieldset } from '../../../molecules/Form/enums/FormFieldsets';
import { FormSubmitButton } from '../../../molecules/Form/components/FormSubmitButton/FormSubmitButton';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { RequiredHelpPhrase } from '../../../../_lib/Form2';
import { TooltipPlacement } from '../../../atoms/Tooltip';

type UserRegistrationTemplateProps = Omit<UserRegistrationFormProps, 'errorType' | 'userRegistrationForm'>;

export function UserRegistrationFormTemplate(props: UserRegistrationTemplateProps) {
  const { t } = useSonTranslation(Namespace.UserRegistrationForm);
  const { inputs, onChange, getStatus, model, submitActionEnabled } = useFormContext();

  const getStatusMessage = (errorType: 'invalidPassword' | undefined) => {
    return {
      [InteractionStatus.FAIL]: FormUtils.createStatusMessage(t(`registerFail.${errorType || 'default'}`)),
      [InteractionStatus.SUCCESS]: FormUtils.createStatusMessage(
        <Trans
          i18nKey={`${Namespace.UserRegistrationForm}:registerSuccess.${props.registrationCompletionType}`}
          components={[
            <Strong key={props.loginPage.url}>
              <Hyperlink
                to={props.loginPage.url}
                className="global--anchor"
                routing={false} // please keep this! links to the login page should not be routed.
              >
                {' ' /* This is a placeholder for the text provided by translation */}
              </Hyperlink>
            </Strong>,
          ]}
        />
      ),
    };
  };

  return (
    <>
      <h2 className="form__headline">{t('headline', { eMail: props.emailAddress })}</h2>
      {props.registrationType !== REGISTRATION_TYPE.selfRegistration && (
        <ContentParagraph>
          <Trans
            i18nKey="UserRegistrationForm:invitationToTeamroom"

            components={[
              <strong className="global-bold" key={props.pageTitle}>
                {props.pageTitle}
              </strong>,
              <strong className="global-bold" key={props.roleName}>
                {props.roleName}
              </strong>,
            ]}
          />
        </ContentParagraph>
      )}
      <RequiredHelpPhrase />
      {model.fieldSets
        .filter(fieldSetParams => fieldSetParams.fieldSet !== FormFieldset.termsOfServiceAndPrivacyPolicy)
        .map(fieldSetParams => {
          // special casing the contactData fieldSet here, since we need to
          // render the phone number related fields in a single FormsRow.
          // see SIBZIEL-9627
          // Rendering a partial for now as a quick way to special case here.
          if (fieldSetParams.fieldSet === FormFieldset.personContactData) {
            return (
              <FieldsetContactData
                key={fieldSetParams.fieldSet}
                fieldSet={fieldSetParams.fieldSet}
                inputs={fieldSetParams.inputs}
              />
            );
          }
          return (
            // @ts-expect-error -- form-typing sucks
            <FormsFieldset key={fieldSetParams.fieldSet} legend={t(`legends.${fieldSetParams.fieldSet}`)}>
              {
                // @ts-expect-error -- form-typing sucks
                fieldSetParams.inputs.map(input => renderFormElement(input, onChange, t))
              }
            </FormsFieldset>
          );
        })}

      <FormsFieldset legend={t('legends.termsOfServiceAndPrivacyPolicy')}>
        <FormsRow>
          <ContentParagraph>
            <Trans
              i18nKey="UserRegistrationForm:readTermsOfServiceAndDataProtection"
              components={[
                <ContentLink
                  key={props.termsOfServiceUrl}
                  to={props.termsOfServiceUrl}
                  target={HyperlinkTarget.BLANK}
                  tooltipPlacement={TooltipPlacement.TOP}
                  newWindowIndicator
                  title={t(`${Namespace.phrases}:linkOpensInNewWindow`)}
                >
                  {t(`${Namespace.glossary}:termsOfService`)}
                </ContentLink>,
                <ContentLink
                  key={props.privacyPolicyUrl}
                  to={props.privacyPolicyUrl}
                  target={HyperlinkTarget.BLANK}
                  tooltipPlacement={TooltipPlacement.TOP}
                  newWindowIndicator
                  title={t(`${Namespace.phrases}:linkOpensInNewWindow`)}
                >
                  {t(`${Namespace.glossary}:privacyPolicy`)}
                </ContentLink>,
              ]}
            />
          </ContentParagraph>
        </FormsRow>

        {
          // @ts-expect-error -- form-typing sucks
          renderFormElement(inputs.agreeTermsOfService, onChange, t)
        }

        <FormsRow>
          <ContentParagraph>
            {props.personalDataProcessingText || t('agreePersonalDataProcessingText')}
          </ContentParagraph>
        </FormsRow>

        {
          // @ts-expect-error -- form-typing sucks
          renderFormElement(inputs.agreePersonalDataProcessing, onChange, t)
        }
      </FormsFieldset>
      <FormSubmitButton buttonLabel={t('sendRegistration')} enabled={submitActionEnabled()} />
      <LiveRegion invisible={false} htmlTag="div">
        {
          // @ts-expect-error -- form-typing sucks
          getStatus(getStatusMessage(model?.response?.data?.errorType))
        }
      </LiveRegion>
    </>
  );
}
